import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ChevronRight,
    ExternalLink,
    Loader2,
    RefreshCw,
    ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "./constants";

export function Badge({ children, className = "" }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${className}`}
        >
            {children}
        </span>
    );
}

export function KpiCard({ label, value, hint, accent = "gold", onClick, active = false }) {
    const ring =
        accent === "red"
            ? "border-rose-500/35 bg-gradient-to-br from-rose-500/10 to-transparent"
            : accent === "amber"
              ? "border-amber-500/35 bg-gradient-to-br from-amber-500/10 to-transparent"
              : accent === "emerald"
                ? "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent"
                : "border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/5 to-[#121A2F]/90";

    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`rounded-xl border p-4 text-left transition-all ${ring} ${
                onClick ? "hover:border-[#D4AF37]/50 hover:scale-[1.02] cursor-pointer" : ""
            } ${active ? "ring-2 ring-[#D4AF37]/40" : ""}`}
        >
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
            <div className="font-display text-2xl md:text-3xl text-white mt-1 tabular-nums">
                {value ?? "—"}
            </div>
            {hint ? <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">{hint}</div> : null}
            {onClick ? (
                <div className="text-[10px] text-[#D4AF37]/80 mt-2 flex items-center gap-0.5">
                    Detaya git <ChevronRight className="h-3 w-3" />
                </div>
            ) : null}
        </Tag>
    );
}

export function MerkezHero({ loading, onRefresh, portalLink, children }) {
    return (
        <header className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#121A2F] via-[#0f1628] to-[#1a1020] p-5 md:p-7">
            <div
                className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-[#8b1f2f]/20 blur-3xl"
                aria-hidden
            />
            <div
                className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-[#D4AF37]/10 blur-3xl"
                aria-hidden
            />
            <div className="relative flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                <div className="max-w-2xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#F3E5AB] mb-3">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Operasyon Hub
                    </div>
                    <h1 className="font-display text-2xl md:text-3xl text-white tracking-tight">
                        Müvekkil Merkezi
                    </h1>
                    <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                        Müvekkil portalından gelen evrak, mesaj ve dosya aktivitesini tek merkezden
                        yönetin. Onay → iletişim → eksik evrak → tahkim akışı burada birleşir.
                    </p>
                    {children}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-[#D4AF37]/35 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        onClick={onRefresh}
                        disabled={loading}
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                        ) : (
                            <RefreshCw className="h-4 w-4 mr-1.5" />
                        )}
                        Verileri yenile
                    </Button>
                    <a
                        href={portalLink || "https://mbshukuk.com/panel/dashboard/"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/10 text-slate-300 hover:border-[#8b1f2f]/50 hover:text-white transition"
                    >
                        <ExternalLink className="h-3.5 w-3.5" /> Müvekkil portalı
                    </a>
                </div>
            </div>
        </header>
    );
}

export function SectionIntro({ icon: Icon, title, description, actions }) {
    return (
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4 pb-3 border-b border-white/5">
            <div>
                <h2 className="font-display text-lg text-white flex items-center gap-2">
                    {Icon ? <Icon className="h-5 w-5 text-[#D4AF37]" /> : null}
                    {title}
                </h2>
                {description ? (
                    <p className="text-sm text-slate-500 mt-1 max-w-3xl leading-relaxed">{description}</p>
                ) : null}
            </div>
            {actions ? <div className="flex flex-wrap gap-2 shrink-0">{actions}</div> : null}
        </div>
    );
}

export function PriorityQueue({ items, onNavigate }) {
    if (!items?.length) {
        return (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4 text-sm text-emerald-200/90">
                Bugün acil bekleyen iş yok — harika! Yine de Canlı Akış sekmesini kontrol edebilirsiniz.
            </div>
        );
    }
    const severityStyle = {
        high: "border-rose-500/35 bg-rose-500/8",
        medium: "border-amber-500/35 bg-amber-500/8",
        low: "border-sky-500/30 bg-sky-500/5",
    };
    return (
        <ul className="space-y-2">
            {items.map((item) => (
                <li key={item.id}>
                    <button
                        type="button"
                        onClick={() => onNavigate?.(item.action_tab || item.id)}
                        className={`w-full text-left rounded-xl border px-4 py-3 flex items-center justify-between gap-3 transition hover:border-[#D4AF37]/40 group ${
                            severityStyle[item.severity] || severityStyle.medium
                        }`}
                    >
                        <div>
                            <div className="text-sm font-medium text-white">{item.title}</div>
                            {item.description ? (
                                <div className="text-xs text-slate-400 mt-0.5">{item.description}</div>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {item.count != null ? (
                                <span className="font-display text-xl text-[#F3E5AB] tabular-nums">
                                    {item.count}
                                </span>
                            ) : null}
                            <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-[#D4AF37] transition" />
                        </div>
                    </button>
                </li>
            ))}
        </ul>
    );
}

export function QuickLinkGrid({ links }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {links.map((link) => {
                const Icon = link.icon;
                return (
                    <Link
                        key={link.to}
                        to={link.to}
                        className="group rounded-xl border border-white/8 bg-[#0B0F19]/50 p-3 hover:border-[#D4AF37]/35 hover:bg-[#D4AF37]/5 transition"
                    >
                        <Icon className="h-4 w-4 text-[#D4AF37] mb-2" />
                        <div className="text-xs font-semibold text-white group-hover:text-[#F3E5AB]">
                            {link.label}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{link.hint}</div>
                    </Link>
                );
            })}
        </div>
    );
}

export function WorkflowStrip({ steps, activeTab, onStepClick }) {
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {steps.map((s) => (
                <button
                    key={s.step}
                    type="button"
                    onClick={() => onStepClick?.(s.tab)}
                    className={`rounded-xl border p-3 text-left transition ${
                        activeTab === s.tab
                            ? "border-[#D4AF37]/50 bg-[#D4AF37]/10"
                            : "border-white/8 bg-[#0B0F19]/40 hover:border-white/15"
                    }`}
                >
                    <div className="text-[10px] text-[#D4AF37] font-bold">ADIM {s.step}</div>
                    <div className="text-xs font-semibold text-white mt-1">{s.title}</div>
                    <div className="text-[10px] text-slate-500 mt-1 leading-snug">{s.detail}</div>
                </button>
            ))}
        </div>
    );
}

export function TabSidebar({ tabs, activeTab, onTabChange, ozet }) {
    return (
        <nav className="flex flex-col gap-1 lg:sticky lg:top-4">
            {tabs.map((t) => {
                const Icon = t.icon;
                const isActive = activeTab === t.id;
                let badge = null;
                if (t.id === "onay" && ozet?.evrak_portal_bekleyen > 0) {
                    badge = ozet.evrak_portal_bekleyen;
                } else if (t.id === "mesaj" && ozet?.mesaj_bekleyen_dosya > 0) {
                    badge = ozet.mesaj_bekleyen_dosya;
                } else if (t.id === "eksik" && ozet?.eksik_evrakli_dosya > 0) {
                    badge = ozet.eksik_evrakli_dosya;
                }
                return (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => onTabChange(t.id)}
                        className={`text-left rounded-xl px-3 py-2.5 transition border ${
                            isActive
                                ? "bg-[#8b1f2f]/20 border-[#8b1f2f]/45 text-white"
                                : "border-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-[#F3E5AB]" : ""}`} />
                            <span className="text-xs font-semibold flex-1">{t.label}</span>
                            {badge != null ? (
                                <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-[#8b1f2f] text-[10px] font-bold text-white flex items-center justify-center">
                                    {badge > 99 ? "99+" : badge}
                                </span>
                            ) : null}
                        </div>
                        {isActive && t.description ? (
                            <p className="text-[10px] text-slate-500 mt-1.5 pl-6 leading-relaxed">
                                {t.description}
                            </p>
                        ) : null}
                    </button>
                );
            })}
        </nav>
    );
}

export function ActivityTimeline({ items }) {
    if (!items?.length) {
        return <p className="text-sm text-slate-500 py-10 text-center">Henüz aktivite kaydı yok.</p>;
    }
    const typeColor = {
        EVRAK: "bg-amber-500",
        DURUM: "bg-sky-500",
        ODEME: "bg-emerald-500",
    };
    return (
        <ol className="relative border-l border-white/10 ml-3 space-y-4 pl-6">
            {items.map((a) => (
                <li key={a.id} className="relative">
                    <span
                        className={`absolute -left-[1.55rem] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-[#0B0F19] ${
                            typeColor[a.event_type] || "bg-slate-500"
                        }`}
                    />
                    <div className="rounded-xl border border-white/8 bg-[#121A2F]/60 px-4 py-3">
                        <div className="flex flex-wrap justify-between gap-2">
                            <div>
                                <div className="text-sm font-medium text-white">{a.title}</div>
                                <Badge className="mt-1 border-white/15 text-slate-400 bg-white/5">
                                    {a.event_label}
                                </Badge>
                            </div>
                            <time className="text-[10px] text-slate-500 shrink-0">
                                {a.created_at
                                    ? new Date(a.created_at).toLocaleString("tr-TR")
                                    : ""}
                            </time>
                        </div>
                        {a.message ? (
                            <p className="text-xs text-slate-400 mt-2">{a.message}</p>
                        ) : null}
                        {a.dosya_link ? (
                            <Link
                                to={a.dosya_link}
                                className="inline-flex items-center gap-1 text-[11px] text-[#D4AF37] mt-2 hover:underline"
                            >
                                {a.dosya_no || "Dosya"} <ChevronRight className="h-3 w-3" />
                            </Link>
                        ) : null}
                    </div>
                </li>
            ))}
        </ol>
    );
}

export function PanelCard({ children, className = "" }) {
    return (
        <section
            className={`rounded-xl border border-[#D4AF37]/15 bg-[#121A2F]/70 backdrop-blur-sm p-4 md:p-5 ${className}`}
        >
            {children}
        </section>
    );
}

export function StatPill({ label, value, tone = "neutral" }) {
    const tones = {
        neutral: "border-white/10 text-slate-300",
        warn: "border-amber-500/30 text-amber-200",
        ok: "border-emerald-500/30 text-emerald-200",
    };
    return (
        <div className={`rounded-lg border px-3 py-2 ${tones[tone] || tones.neutral}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-sm font-semibold mt-0.5">{value}</div>
        </div>
    );
}
